import { createColumns, IColumnsGroup, ITableHeader } from "@/utils/ColumnsCreator";
import { computed, ComputedRef } from "vue";
import { useWindowResize } from "@/utils/useWindowSize";
import { CreatedColumns } from "@/services/columns/columns";
import { IColumnFilter, IFilter } from "@/types";
import { CreatedFilters } from "@/services/filters/filters";
import { filterClass } from "@/services/filters/filterClass";
import { getCurrentDate } from "@/utils/formatDates";
import { getAffiliateSearch, getActiveUserSearch } from "@/api/user";
import { getAdvertisersSearch } from "@/api/revenue";
import { getEmployeesOffersV2 } from "@/api/offers";
import { translatePartnerStatus, translateOffersStatus, translateAdvertiserStatus } from "@/utils/translate";
import store from "@/store";
import router from "@/router";

export const holdDefaultColumns = [
    'date',
    'master_id',
    'master_name',
    'master_manager_name',
    'affiliate_id',
    'affiliate_name',
    'advertiser_id',
    'advertiser_name',
    'advertiser_manager_name',
    'offer_id',
    'offer_name',
    'offer_currency',
    'offer_goal_id',
    'offer_goal_name',
    'new_approved_payout',
    'old_approved_payout',
    'adjust_payout',
    'hold_expired_payout',
    'total_payout',
];

export const useHold = (): { headers: ITableHeader[]; summaryHeaders: ITableHeader[]; getColumns: () => IColumnsGroup[]; getDefaultColumns: ComputedRef<string[]> } => {
    const headers: ITableHeader[] = [
        { text: 'Открыть', sortable: false, value: 'action', align: 'center', width: '50px', fixed: true },
        { text: 'Дата', sortable: false, value: 'date', align: 'right', width: '110px' },
        { text: 'Валюта', sortable: false, value: 'offer_currency', width: '80px' },
        { text: 'ID мастера', sortable: false, value: 'master_id', width: '100px' },
        { text: 'Мастер', sortable: false, value: 'master_name', width: '150px' },
        { text: 'ID партнёра', sortable: false, value: 'affiliate_id', width: '100px' },
        { text: 'Партнёр', sortable: false, value: 'affiliate_name', width: '150px' },
        { text: 'Менеджер партнёра', sortable: false, value: 'master_manager_name', width: '150px' },
        { text: 'ID рекламодателя', sortable: false, value: 'advertiser_id', width: '120px' },
        { text: 'Рекламодатель', sortable: false, value: 'advertiser_name', width: '150px' },
        { text: 'ID оффера', sortable: false, value: 'offer_id', width: '100px' },
        { text: 'Оффер', sortable: false, value: 'offer_name', width: '150px' },
        { text: 'ID цели', sortable: false, value: 'offer_goal_id', width: '100px' },
        { text: 'Цель', sortable: false, value: 'offer_goal_name', width: '150px' },
        { text: 'Менеджер рекламодателя', sortable: false, value: 'advertiser_manager_name', width: '180px' },
        { text: '∑ новых принятых', sortable: false, value: 'new_approved_payout', align: 'right', width: '150px' },
        { text: '∑ старых принятых', sortable: false, value: 'old_approved_payout', align: 'right', width: '150px' },
        { text: '∑ корректировок', sortable: false, value: 'adjust_payout', align: 'right', width: '150px' },
        { text: '∑ вышедших из холда', sortable: false, value: 'hold_expired_payout', align: 'right', width: '170px' },
        { text: '∑ Итого', sortable: false, value: 'total_payout', align: 'right', width: '120px' },
    ];

    const summaryHeaders = [
        { text: 'Валюта', sortable: false, value: 'offer_currency', width: 'auto', align: 'right' },
        { text: '∑ новых принятых', sortable: false, value: 'new_approved_payout', align: 'right', width: '150px' },
        { text: '∑ старых принятых', sortable: false, value: 'old_approved_payout', align: 'right', width: '150px' },
        { text: '∑ корректировок', sortable: false, value: 'adjust_payout', align: 'right', width: '150px' },
        { text: '∑ вышедших из холда', sortable: false, value: 'hold_expired_payout', align: 'right', width: '170px' },
        { text: '∑ Итого', sortable: false, value: 'total_payout', align: 'right', width: '120px' },
    ];

    const getDefaultColumns = computed(() => {
        const { width } = useWindowResize();
        return width.value >= 768 ? holdDefaultColumns : holdDefaultColumns.slice(0, 8);
    });

    const getColumns = (): IColumnsGroup[] => {
        return createColumns(headers, getDefaultColumns.value);
    };

    return { headers, summaryHeaders, getColumns, getDefaultColumns };
};

export class HoldColumns extends CreatedColumns {
    constructor() {
        super();
    }

    getColumns(): IColumnFilter[] {
        const isAffiliateRoute = router.currentRoute.name === 'affiliate';
        const isMaster = store.state.isMasterAffiliate;

        const hiddenCols = new Set<string>(
            isAffiliateRoute
                ? isMaster
                    ? ['master_id', 'master_name']
                    : ['master_id', 'master_name', 'affiliate_id', 'affiliate_name']
                : [],
        );

        return [
            {
                name: 'Общее',
                items: [
                    { title: 'Дата', value: 'date', default: true },
                    { title: 'Валюта', value: 'offer_currency', default: true, disabled: true },
                ],
            },
            {
                name: 'Партнёр',
                items: [
                    { title: 'ID мастера', value: 'master_id', default: true },
                    { title: 'Мастер', value: 'master_name', default: true },
                    { title: 'ID партнёра', value: 'affiliate_id', default: true },
                    { title: 'Партнёр', value: 'affiliate_name', default: true },
                    { title: 'Менеджер партнёра', value: 'master_manager_name', default: true },
                ].filter(item => !hiddenCols.has(item.value)),
            },
            {
                name: 'Рекламодатель',
                items: [
                    { title: 'ID рекламодателя', value: 'advertiser_id', default: true },
                    { title: 'Рекламодатель', value: 'advertiser_name', default: true },
                    { title: 'Менеджер рекламодателя', value: 'advertiser_manager_name', default: true },
                ],
            },
            {
                name: 'Оффер',
                items: [
                    { title: 'ID оффера', value: 'offer_id', default: true },
                    { title: 'Оффер', value: 'offer_name', default: true },
                    { title: 'ID цели', value: 'offer_goal_id', default: true },
                    { title: 'Цель', value: 'offer_goal_name', default: true },
                ],
            },
            {
                name: 'Итоги',
                items: [
                    { title: '∑ новых принятых', value: 'new_approved_payout', default: true },
                    { title: '∑ старых принятых', value: 'old_approved_payout', default: true },
                    { title: '∑ корректировок', value: 'adjust_payout', default: true },
                    { title: '∑ вышедших из холда', value: 'hold_expired_payout', default: true },
                    { title: '∑ итого', value: 'total_payout', default: true },
                ],
            },
        ];
    }
}

export class HoldFilters extends CreatedFilters {
    constructor() {
        super();
    }

    getFilters(): IFilter[] {
        const today = getCurrentDate('yyyy-MM-dd');
        const isAffiliateRoute = this.checkRoute('affiliate');
        const isMaster = store.state.isMasterAffiliate;
        const affiliate = store.getters['GET_CURRENT_AFFILIATE'];

        return [
            new filterClass.Datepicker({
                id: 'date',
                label: '',
                pickerType: 'date',
                select: [],
                defaultValue: [today, today],
                range: true,
                operator: 'between',
                clearable: true,
                isHidden: true,
            }),
            new filterClass.Multiselect({
                col: 1,
                colName: 'Общее',
                id: 'offer_currency',
                label: 'Валюта',
                noDataText: 'Нет выбранной валюты',
                select: [],
                items: [],
                itemValue: 'code',
                itemText: 'code',
                type: 'string',
                isCurrencyCatalogue: true,
                operator: 'in',
            }),
            new filterClass.Autocomplete({
                col: 1,
                colName: 'Партнёр',
                id: 'master_id',
                apiMethod: getAffiliateSearch,
                itemValue: 'id',
                itemText: 'name',
                label: 'Мастер-аккаунт',
                template: (item: any): string => `${item.id!}`,
                select: isAffiliateRoute && isMaster && affiliate ? [{ id: affiliate.id, name: affiliate.name }] : [],
                defaultValue: isAffiliateRoute && isMaster && affiliate ? [{ id: affiliate.id, name: affiliate.name }] : undefined,
                multiple: true,
                multiPaste: true,
                operator: 'in',
                isHidden: isAffiliateRoute,
            }),
            new filterClass.Autocomplete({
                col: 1,
                colName: 'Партнёр',
                id: 'affiliate_id',
                apiMethod: isAffiliateRoute && !isMaster ? undefined : getAffiliateSearch,
                label: 'Партнёр',
                itemValue: 'id',
                itemText: 'name',
                template: (i) => `${i.id} ${i.first_name} ${i.last_name} ${i.email}`,
                select: isAffiliateRoute && !isMaster && affiliate ? [{ id: affiliate.id, name: affiliate.name }] : [],
                defaultValue: isAffiliateRoute && !isMaster && affiliate ? [{ id: affiliate.id, name: affiliate.name }] : undefined,
                items: [],
                multiple: true,
                multiPaste: true,
                operator: 'in',
                isHidden: isAffiliateRoute && !isMaster,
            }),
            new filterClass.Multiselect({
                col: 1,
                colName: 'Партнёр',
                id: 'affiliate_status',
                label: 'Статус партнёра',
                select: [],
                clearable: true,
                itemValue: 'id',
                items: [
                    { id: 'active' },
                    { id: 'pending' },
                    { id: 'deleted' },
                    { id: 'blocked' },
                    { id: 'rejected' },
                ],
                template: ({ id }) => this.translateStatus(id, translatePartnerStatus),
                itemText: ({ id }) => this.translateStatus(id, translatePartnerStatus),
                operator: 'in',
            }),
            new filterClass.Autocomplete({
                col: 1,
                colName: 'Партнёр',
                id: 'master_manager_id',
                apiMethod: getActiveUserSearch,
                key: 'search',
                label: 'Менеджер партнёра',
                itemValue: 'id',
                itemText: 'name',
                multiple: true,
                multiPaste: true,
                operator: 'in',
            }),
            new filterClass.Multiselect({
                col: 1,
                colName: 'Партнёр',
                id: 'master_manager_department_id',
                label: 'Отдел менеджера',
                noDataText: 'Нет данных',
                select: [],
                items: [],
                type: 'number',
                itemValue: 'id',
                itemText: 'name',
                template: ({ name }) => name,
                operator: 'in',
            }),
            new filterClass.Autocomplete({
                col: 2,
                colName: 'Рекламодатель',
                id: 'advertiser_id',
                apiMethod: getAdvertisersSearch,
                label: 'Рекламодатель',
                itemValue: 'id',
                itemText: 'name',
                multiple: true,
                multiPaste: true,
                operator: 'in',
            }),
            new filterClass.Multiselect({
                col: 2,
                colName: 'Рекламодатель',
                id: 'advertiser_status',
                label: 'Статус рекламодателя',
                items: [{ id: 'active' }, { id: 'pending' }, { id: 'deleted' }],
                select: [],
                clearable: true,
                itemValue: 'id',
                itemText: ({ id }) => (this.translateStatus(id, translateAdvertiserStatus)),
                template: ({ id }) => (this.translateStatus(id, translateAdvertiserStatus)),
                operator: 'in',
            }),
            new filterClass.Autocomplete({
                col: 2,
                colName: 'Рекламодатель',
                id: 'advertiser_manager_id',
                apiMethod: getActiveUserSearch,
                key: 'search',
                label: 'Менеджер рекламодателя',
                itemValue: 'id',
                itemText: 'name',
                multiple: true,
                multiPaste: true,
                operator: 'in',
            }),
            new filterClass.Autocomplete({
                col: 3,
                colName: 'Оффер',
                id: 'offer_id',
                apiMethod: getEmployeesOffersV2,
                key: 'search',
                label: 'Оффер',
                itemValue: 'legacy_id',
                itemText: 'name',
                multiple: true,
                multiPaste: true,
                operator: 'in',
            }),
            new filterClass.Multiselect({
                col: 3,
                colName: 'Оффер',
                id: 'offer_status',
                label: 'Статус оффера',
                select: [],
                clearable: true,
                itemValue: 'id',
                items: [
                    { id: 'active' },
                    { id: 'paused' },
                    { id: 'deleted' },
                    { id: 'pending' },
                    { id: 'expired' },
                ],
                template: ({ id }) => (this.translateStatus(id, translateOffersStatus)),
                itemText: ({ id }) => (this.translateStatus(id, translateOffersStatus)),
                operator: 'in',
            }),
            new filterClass.Multiselect({
                col: 3,
                colName: 'Оффер',
                id: 'offer_goal_id',
                label: 'Цель',
                noDataText: 'Выберите оффер',
                select: [],
                items: [],
                itemValue: 'legacy_id',
                disabled: true,
                tooltip: 'Сначала отфильтруйте по офферу',
                operator: 'in',
                itemText: ({ legacy_id, name }) => `${legacy_id} ${name}`,
                template: ({ legacy_id, name }) => `${legacy_id} ${name}`,
            }),
            new filterClass.Multiselect({
                col: 3,
                colName: 'Оффер',
                id: 'offer_goal_status',
                label: 'Статус цели',
                items: [{ id: 'active' }, { id: 'deleted' }],
                select: [],
                clearable: true,
                itemValue: 'id',
                itemText: ({ id }) => id === 'active' ? 'Активна' : 'Удалена',
                template: ({ id }) => id === 'active' ? 'Активна' : 'Удалена',
                operator: 'in',
            }),
            new filterClass.Multiselect({
                col: 3,
                colName: 'Оффер',
                id: 'offer_department',
                label: 'Направление',
                noDataText: 'Нет данных',
                select: [],
                items: [],
                type: 'number',
                itemValue: 'id',
                itemText: 'name',
                template: ({ name }) => name,
                operator: 'in',
            }),
            new filterClass.Multiselect({
                col: 4,
                id: 'offer_country',
                label: 'Страна',
                noDataText: 'Нет данных',
                select: [],
                items: [],
                type: 'number',
                itemValue: 'id',
                itemText: 'name',
                template: ({ name }) => name,
                operator: 'in',
            }),
            new filterClass.Multiselect({
                col: 4,
                id: 'offer_product',
                label: 'Тип продукта',
                noDataText: 'Нет данных',
                select: [],
                items: [],
                type: 'number',
                itemValue: 'id',
                itemText: 'name',
                template: ({ name }) => name,
                operator: 'in',
            }),
            new filterClass.Multiselect({
                col: 4,
                id: 'offer_target_action',
                type: 'number',
                label: 'Целевое действие',
                noDataText: 'Нет данных',
                select: [],
                items: [],
                itemValue: 'id',
                itemText: 'name',
                template: ({ name }) => name,
                operator: 'in',
            }),
            new filterClass.Multiselect({
                col: 4,
                id: 'offer_stats',
                label: 'Тип статистики',
                items: [],
                select: [],
                clearable: true,
                type: 'number',
                itemValue: 'id',
                itemText: 'name',
                template: ({ name }) => name,
                operator: 'in',
            }),
            new filterClass.Multiselect({
                col: 4,
                id: 'offer_tag',
                label: 'Теги оффера',
                noDataText: 'Нет данных',
                select: [],
                items: [],
                type: 'number',
                itemValue: 'id',
                itemText: 'name',
                template: ({ name }) => name,
                operator: 'in',
            }),
        ];
    }

    prepareFiltersForSend(selectedFilters: { [key: string]: any }): any[] {
        const filters: {
            column: string;
            operator: string;
            values: any;
        }[] = [];
        for (const key in selectedFilters) {
            if (key === 'date_to') continue;
            if (key === 'date_from') {
                const filter = this.filters.find((filter) => filter.id === 'date')!;
                const item = {
                    column: filter.id!,
                    operator: filter.operator!,
                    values: [selectedFilters['date_from'], selectedFilters['date_to']],
                };
                filters.push(item);
            } else {
                const filter = this.filters.find((filter) => filter.id === key);
                if (!filter) continue;
                const value = selectedFilters[key];
                const values = Array.isArray(value) ? value : [value];
                const item = {
                    column: filter.id!,
                    operator: filter.operator!,
                    values,
                };
                filters.push(item);
            }
        }
        return filters;
    }
}
